import { useCallback } from 'react'
import { useI18n } from '../../i18n'
import type { CVEducation } from '../../types/cv'
import './editor.css'

export interface EducationEditorProps {
  data: CVEducation[]
  onChange: (data: CVEducation[]) => void
}

/**
 * Education section editor
 */
export function EducationEditor({ data, onChange }: EducationEditorProps) {
  const { t } = useI18n()
  const handleAdd = useCallback(() => {
    const newEntry: CVEducation = {
      id: crypto.randomUUID(),
      institution: '',
      degree: '',
      fieldOfStudy: '',
      location: '',
      startDate: '',
      endDate: null,
      isCurrent: false,
      description: '',
      grade: '',
      sortOrder: data.length,
    }
    onChange([...data, newEntry])
  }, [data, onChange])

  const handleRemove = useCallback(
    (id: string) => {
      onChange(data.filter((item) => item.id !== id))
    },
    [data, onChange]
  )

  const handleChange = useCallback(
    (id: string, field: keyof CVEducation, value: string | boolean | null) => {
      onChange(data.map((item) => (item.id === id ? { ...item, [field]: value } : item)))
    },
    [data, onChange]
  )

  return (
    <>
      <ul className="editor-entry-list" aria-label={t('editor.lists.education')}>
        {data.map((edu, index) => {
          const entryLabel =
            edu.degree || edu.institution || t('editor.entries.education', { number: index + 1 })
          return (
            <li key={edu.id} className="editor-entry" aria-label={entryLabel}>
              <div className="editor-entry-header">
                <span className="editor-entry-title">{entryLabel}</span>
                <div className="editor-entry-actions">
                  <button
                    type="button"
                    className="editor-btn editor-btn-danger editor-btn-icon"
                    onClick={() => handleRemove(edu.id)}
                    aria-label={t('actions.remove', { item: entryLabel })}
                  >
                    ×
                  </button>
                </div>
              </div>

              <div className="editor-grid">
                <div className="editor-field">
                  <label htmlFor={`edu-institution-${edu.id}`}>{t('fields.institution')}</label>
                  <input
                    id={`edu-institution-${edu.id}`}
                    type="text"
                    value={edu.institution}
                    onChange={(e) => handleChange(edu.id, 'institution', e.target.value)}
                    placeholder={t('placeholders.institution')}
                  />
                </div>

                <div className="editor-field">
                  <label htmlFor={`edu-degree-${edu.id}`}>{t('fields.degree')}</label>
                  <input
                    id={`edu-degree-${edu.id}`}
                    type="text"
                    value={edu.degree}
                    onChange={(e) => handleChange(edu.id, 'degree', e.target.value)}
                    placeholder={t('placeholders.degree')}
                  />
                </div>

                <div className="editor-field">
                  <label htmlFor={`edu-fieldOfStudy-${edu.id}`}>{t('fields.fieldOfStudy')}</label>
                  <input
                    id={`edu-fieldOfStudy-${edu.id}`}
                    type="text"
                    value={edu.fieldOfStudy}
                    onChange={(e) => handleChange(edu.id, 'fieldOfStudy', e.target.value)}
                    placeholder={t('placeholders.fieldOfStudy')}
                  />
                </div>

                <div className="editor-field">
                  <label htmlFor={`edu-location-${edu.id}`}>{t('fields.location')}</label>
                  <input
                    id={`edu-location-${edu.id}`}
                    type="text"
                    value={edu.location}
                    onChange={(e) => handleChange(edu.id, 'location', e.target.value)}
                    placeholder={t('placeholders.location')}
                  />
                </div>

                <div className="editor-date-range">
                  <div className="editor-field">
                    <label htmlFor={`edu-startDate-${edu.id}`}>{t('fields.startDate')}</label>
                    <input
                      id={`edu-startDate-${edu.id}`}
                      type="month"
                      value={edu.startDate}
                      onChange={(e) => handleChange(edu.id, 'startDate', e.target.value)}
                    />
                  </div>

                  <div className="editor-field">
                    <label htmlFor={`edu-endDate-${edu.id}`}>{t('fields.endDate')}</label>
                    <input
                      id={`edu-endDate-${edu.id}`}
                      type="month"
                      value={edu.endDate || ''}
                      onChange={(e) => handleChange(edu.id, 'endDate', e.target.value || null)}
                      disabled={edu.isCurrent}
                      aria-disabled={edu.isCurrent}
                    />
                  </div>
                </div>

                <div className="editor-field">
                  <label className="editor-checkbox">
                    <input
                      type="checkbox"
                      checked={edu.isCurrent}
                      onChange={(e) => {
                        handleChange(edu.id, 'isCurrent', e.target.checked)
                        if (e.target.checked) {
                          handleChange(edu.id, 'endDate', null)
                        }
                      }}
                    />
                    {t('fields.currentEducation')}
                  </label>
                </div>

                <div className="editor-field">
                  <label htmlFor={`edu-grade-${edu.id}`}>{t('fields.grade')}</label>
                  <input
                    id={`edu-grade-${edu.id}`}
                    type="text"
                    value={edu.grade}
                    onChange={(e) => handleChange(edu.id, 'grade', e.target.value)}
                    placeholder={t('placeholders.grade')}
                  />
                </div>

                <div className="editor-field editor-field-full">
                  <label htmlFor={`edu-description-${edu.id}`}>{t('fields.description')}</label>
                  <textarea
                    id={`edu-description-${edu.id}`}
                    className="editor-textarea-expanded"
                    value={edu.description}
                    onChange={(e) => handleChange(edu.id, 'description', e.target.value)}
                    placeholder={t('placeholders.educationDescription')}
                    rows={5}
                  />
                </div>
              </div>
            </li>
          )
        })}
      </ul>

      <button type="button" className="editor-add-btn" onClick={handleAdd}>
        <span aria-hidden="true">+</span> {t('actions.addEducation')}
      </button>
    </>
  )
}
